import {
    faChevronDown,
    faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
    createColumnHelper,
    type ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    useReactTable,
    type VisibilityState,
} from "@tanstack/react-table";
import {
    type HTMLProps,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { Equipment } from "../../apiClient/generated";
import { useInfrastructureApiClient } from "../../hooks";
import { CoordinatesField } from "../../ui/GenericFields";
import { formatDate } from "../../utils/dates";
import { CompactTableUI } from "../DataTable/CompactTableUI";
import { useStable } from "../MapV2/hooks/areaFilter";

export interface EquipmentWithChildren extends Equipment {
    childrenData: EquipmentWithChildren[];
}

const convertToNestedTree = (items: Equipment[]): EquipmentWithChildren[] => {
    const itemMap: Record<string, EquipmentWithChildren> = {};

    // Step 1: Create a map of items keyed by their IDs
    items.forEach((item) => {
        itemMap[item.id] = {
            ...item,
            childrenData: [],
        };
    });

    // Step 2: Assign each item to its parent's children array
    items.forEach((item) => {
        if (item.parent && itemMap[item.parent]) {
            const parent = itemMap[item.parent];
            if (!parent.children) {
                parent.children = [];
            }
            parent.childrenData.push(itemMap[item.id]); // Push itemMap[item.id] instead of item
        }
    });

    // Step 3: Form the tree structure starting from root items
    const rootItems: EquipmentWithChildren[] = [];
    items.forEach((item) => {
        if (!item.parent) {
            rootItems.push(itemMap[item.id]); // Push itemMap[item.id] instead of item
        }
    });

    return rootItems;
};

const columnHelper = createColumnHelper<EquipmentWithChildren>();

function IndeterminateCheckbox({
    indeterminate,
    className = "",
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = useRef<HTMLInputElement>(null!);

    useEffect(() => {
        if (typeof indeterminate === "boolean") {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate, rest.checked]);

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + " cursor-pointer"}
            {...rest}
        />
    );
}

// Column IDs
const COLUMN_IDS = {
    // Tree column (always visible)
    TREE: "tree",

    // Basic Info
    SITE_NAME: "site",
    LOCATION: "location",

    // Manufacturer
    MANUFACTURER_MAKE: "manufacturerMake",
    MANUFACTURER_MODEL: "manufacturerModel",

    // Operator
    OPERATOR_STATUS: "operatorStatus",
    OPERATOR_UNIQUE_IDS: "operatorUniqueIds",

    // Dates
    DATE_OF_INSTALLATION: "dateOfInstallation",

    // Additional fields
    MATERIAL: "material",
    FUEL: "fuel",
} as const;

// Fallback column visibility (used when no props provided)
const FALLBACK_COLUMN_VISIBILITY: VisibilityState = {
    // Visible by default
    [COLUMN_IDS.SITE_NAME]: false,
    [COLUMN_IDS.LOCATION]: false,
    [COLUMN_IDS.MANUFACTURER_MAKE]: true,
    [COLUMN_IDS.MANUFACTURER_MODEL]: true,
    [COLUMN_IDS.OPERATOR_STATUS]: true,
    [COLUMN_IDS.DATE_OF_INSTALLATION]: true,
    [COLUMN_IDS.MATERIAL]: false,
    [COLUMN_IDS.FUEL]: false,
    [COLUMN_IDS.OPERATOR_UNIQUE_IDS]: true,
};

// Build columns based on selection enabled
const buildColumns = (enableSelection: boolean) => {
    const columns = [];

    // Add selection column if enabled
    if (enableSelection) {
        columns.push(
            columnHelper.display({
                id: "select",
                header: ({ table }) => (
                    <IndeterminateCheckbox
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({ row }) => (
                    <div style={{ paddingLeft: `${row.depth * 2}rem` }}>
                        <IndeterminateCheckbox
                            checked={row.getIsSelected()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                        />
                    </div>
                ),
            }),
        );
    }

    // Tree column
    const treeColumn = columnHelper.accessor("equipmentClass", {
        id: COLUMN_IDS.TREE,
        cell: ({ row }) => {
            const name =
                row.original.equipmentClass || row.original.groupName || "-";
            const childCount = row.original.children.length;
            const displayName =
                childCount > 0 ? `${name} (${childCount})` : name;

            return (
                <div
                    className="flex items-center gap-2 whitespace-nowrap"
                    style={{ paddingLeft: `${row.depth * 2}rem` }}
                >
                    {row.getCanExpand() ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                row.getToggleExpandedHandler()();
                            }}
                            className="flex items-center"
                        >
                            <FontAwesomeIcon
                                icon={
                                    row.getIsExpanded()
                                        ? faChevronDown
                                        : faChevronRight
                                }
                                className="w-3 h-3"
                            />
                        </button>
                    ) : (
                        <span className="w-3" />
                    )}
                    <span className="overflow-hidden truncate">
                        {displayName}
                    </span>
                </div>
            );
        },
        header: "Equipment",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
    });

    columns.push(treeColumn);

    // Data columns
    const dataColumns = [
        columnHelper.accessor("siteName", {
            id: COLUMN_IDS.SITE_NAME,
            header: "Site",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("location", {
            id: COLUMN_IDS.LOCATION,
            header: "Location",
            cell: (info) => {
                const value = info.getValue();
                if (value) {
                    return (
                        <CoordinatesField
                            coordinates={value.coordinates}
                            allowCopy={true}
                            mapLinkParams={{
                                siteId: info.row.original.site,
                                lat: value.coordinates[1].toString(),
                                lon: value.coordinates[0].toString(),
                            }}
                        />
                    );
                }
                return "-";
            },
            size: 160,
            enableSorting: false,
        }),

        columnHelper.accessor("manufacturerMake", {
            id: COLUMN_IDS.MANUFACTURER_MAKE,
            header: "Manufacturer",
            cell: (info) => (
                <div className="overflow-hidden truncate">
                    {info.getValue()}
                </div>
            ),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("manufacturerModel", {
            id: COLUMN_IDS.MANUFACTURER_MODEL,
            header: "Model",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("operatorStatus", {
            id: COLUMN_IDS.OPERATOR_STATUS,
            header: "Status",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("dateOfInstallation", {
            id: COLUMN_IDS.DATE_OF_INSTALLATION,
            header: "Installation Date",
            cell: (info) => {
                const value = info.getValue();
                return formatDate(value);
            },
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("material", {
            id: COLUMN_IDS.MATERIAL,
            header: "Material",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor("fuel", {
            id: COLUMN_IDS.FUEL,
            header: "Fuel",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableSorting: false,
        }),

        columnHelper.accessor(
            (row) => ({
                operatorUniqueId: row.operatorUniqueId,
                otherOperatorIds: row.otherOperatorIds,
            }),
            {
                id: COLUMN_IDS.OPERATOR_UNIQUE_IDS,
                header: "Operator IDs",
                cell: (info) => {
                    const value = info.getValue();
                    let opIds = "";
                    if (value.operatorUniqueId.trim()) {
                        opIds = value.operatorUniqueId;
                    }
                    if (value.otherOperatorIds.trim()) {
                        opIds += value.otherOperatorIds.split("¦").join(", ");
                    }
                    if (opIds.trim()) {
                        return opIds;
                    }
                    return "-";
                },
                enableSorting: false,
                enableColumnFilter: false,
            },
        ),
    ];

    columns.push(...dataColumns);

    return columns;
};

interface EquipmentTreeProps {
    siteId: string;

    // Column visibility control
    columnVisibility?: VisibilityState;
    onColumnVisibilityChange?: (visibility: VisibilityState) => void;

    // Initial column visibility (for uncontrolled mode)
    defaultColumnVisibility?: VisibilityState;

    // Selection control (disabled by default)
    enableSelection?: boolean;
    rowSelection?: Record<string, boolean>;
    onRowSelectionChange?: (selection: Record<string, boolean>) => void;

    onClickRow?: (equipment: EquipmentWithChildren) => void;
}

export const EquipmentTree = (props: EquipmentTreeProps) => {
    const apiClient = useInfrastructureApiClient();

    // Return all equipment on site
    const equipmentOnSiteQuery = useQuery({
        queryKey: ["equipmentOnSites", props.siteId],
        queryFn: async () => {
            const response = await apiClient.infrastructureEquipmentList({
                site: [props.siteId],
                withoutParent: true,
                pageSize: 200,
            });
            return response.results;
        },
        refetchOnWindowFocus: false,
    });

    // Equipment to load state handling
    const [equipmentToLoad, setEquipmentToLoad] = useState<string[]>([]);

    // Use Queries, used to load multiple equipment pieces
    const equipmentQueries = useQueries({
        queries: equipmentToLoad.map((id) => ({
            queryKey: ["equipment", id],
            queryFn: async () => {
                const response =
                    await apiClient.infrastructureEquipmentRetrieve({
                        id: id,
                    });
                return response;
            },
            refetchOnWindowFocus: false,
        })),
    });
    const equipmentQueriesData = useStable(equipmentQueries.map((q) => q.data));

    // List of all equipment loaded
    const allEquipment = useMemo(() => {
        return [
            ...(equipmentOnSiteQuery.data ?? []),
            ...equipmentQueriesData.filter((e) => !!e),
        ];
    }, [equipmentOnSiteQuery.data, equipmentQueriesData]);

    // Handler to load equipment children
    const loadEquipmentChildren = useCallback(
        (idsToLoad: string[]) => {
            const uniqueOrderedIds = new Set(equipmentToLoad);
            for (const i of allEquipment) {
                if (idsToLoad.includes(i.id)) {
                    i.children.forEach((childId) =>
                        uniqueOrderedIds.add(childId),
                    );
                }
            }
            setEquipmentToLoad(
                Array.from(uniqueOrderedIds).toSorted((a, b) =>
                    a.localeCompare(b),
                ),
            );
        },
        [allEquipment, equipmentToLoad],
    );

    const equipmentTableData = useMemo(
        () => convertToNestedTree(allEquipment),
        [allEquipment],
    );

    // Column visibility state
    const [internalColumnVisibility, setInternalColumnVisibility] =
        useState<VisibilityState>(
            props.defaultColumnVisibility ?? FALLBACK_COLUMN_VISIBILITY,
        );

    const columnVisibility = props.columnVisibility ?? internalColumnVisibility;
    const onColumnVisibilityChange =
        props.onColumnVisibilityChange ?? setInternalColumnVisibility;

    // Row selection state (only if enabled)
    const [internalRowSelection, setInternalRowSelection] = useState<
        Record<string, boolean>
    >({});

    const rowSelection = props.enableSelection
        ? (props.rowSelection ?? internalRowSelection)
        : {};

    const setRowSelection = props.enableSelection
        ? (props.onRowSelectionChange ?? setInternalRowSelection)
        : undefined;

    // Build columns based on enableSelection prop
    const columns = useMemo(
        () => buildColumns(props.enableSelection ?? false),
        [props.enableSelection],
    );

    // Table variables
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const table = useReactTable({
        data: equipmentTableData,
        columns,
        state: {
            expanded,
            columnVisibility,
            ...(props.enableSelection && { rowSelection }),
        },
        onExpandedChange: (updater) => {
            const newExpanded =
                typeof updater === "function" ? updater(expanded) : updater;
            setExpanded(newExpanded);
            loadEquipmentChildren(Object.keys(newExpanded));
        },
        onColumnVisibilityChange,
        ...(props.enableSelection && {
            onRowSelectionChange: setRowSelection,
            enableRowSelection: true,
        }),
        enableFilters: false,
        enableSorting: false,
        getRowId: (row) => row.id,
        getSubRows: (row) => row.childrenData,
        getRowCanExpand: (row) => row.original.children.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    return (
        <div className="overflow-x-auto w-full h-full">
            <CompactTableUI
                table={table}
                dataName="equipmentOnSite"
                columns={columns}
                loadingData={equipmentOnSiteQuery.isLoading}
                itemCount={table.getRowModel().rows.length}
                showPagination={true}
                onClickRow={
                    props.onClickRow
                        ? (_, row) => props.onClickRow!(row!)
                        : undefined
                }
            />
        </div>
    );
};
